import * as React from "react";
import * as ReactDOM from "react-dom/client";
import { App } from "../ui/App";
import { SessionContext } from "../ui/SessionContext";
import { isInteractionRequired, isNaaSupported, naaSignIn, naaSignOut } from "../auth/naa";

/* global Office, document, console, localStorage */

interface CachedSession {
  username: string;
  accessToken: string;
  expiresOn: string | null;
  graphAccessToken: string | null;
  graphExpiresOn: string | null;
}

const STORAGE_KEY = "epic_session";

function loadSession(): CachedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CachedSession) : null;
  } catch {
    return null;
  }
}

function saveSession(s: CachedSession | null) {
  if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  else localStorage.removeItem(STORAGE_KEY);
}

function runAuthDialog(): Promise<CachedSession> {
  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      `${window.location.origin}/auth.html`,
      { height: 60, width: 30, promptBeforeOpen: false },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(`displayDialogAsync failed: ${result.error?.message}`));
          return;
        }
        const dialog = result.value;
        let settled = false;

        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg: any) => {
          if (settled) return;
          try {
            const msg = JSON.parse(arg.message);
            settled = true;
            dialog.close();
            if (msg.type === "success" && msg.accessToken) {
              resolve({
                username: msg.username,
                accessToken: msg.accessToken,
                expiresOn: msg.expiresOn ?? null,
                graphAccessToken: msg.graphAccessToken ?? null,
                graphExpiresOn: msg.graphExpiresOn ?? null,
              });
            } else {
              reject(new Error(msg.message ?? "Sign-in failed"));
            }
          } catch (e: any) {
            settled = true;
            reject(new Error(`Bad auth message: ${e.message}`));
          }
        });

        dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: any) => {
          if (settled) return;
          if (arg.error === 12006) {
            settled = true;
            reject(new Error("Dialog closed before sign-in completed"));
          } else {
            console.warn("dialog event:", arg);
          }
        });
      }
    );
  });
}

function AuthPrompt({
  onSignIn,
  error,
}: {
  onSignIn: () => void;
  error?: string;
}): JSX.Element {
  return (
    <section className="auth-prompt">
      <h1>EPIC</h1>
      <p>Sign in to see contact information and connect this email to EPIC records.</p>
      <button className="btn-primary" onClick={onSignIn}>
        Sign in
      </button>
      {error && (
        <p className="error-text" style={{ display: "block" }}>
          {error}
        </p>
      )}
    </section>
  );
}

function Root(): JSX.Element {
  // Auth strategy. NAA (brokered, in-iframe) is preferred when the host
  // supports it. If NAA turns out to be unusable at runtime — the
  // brk-multihub redirect isn't registered, or a Conditional Access policy
  // blocks brokering (the NAA FAQ calls this out) — we permanently downgrade
  // this session to the Dialog API flow (runAuthDialog → auth.html) so auth
  // never gets bricked. The ref holds the current (possibly downgraded) path.
  const naaCapable = React.useMemo(() => isNaaSupported(), []);
  const strategyRef = React.useRef<"naa" | "dialog">(naaCapable ? "naa" : "dialog");

  // Seed synchronously from our own localStorage cache (both strategies) so a
  // reopen renders the app instantly instead of flashing "Signing in…". Under
  // NAA the token is then refreshed silently in the background (see the effect
  // below); the "Signing in…" pane only shows on a cold first open, when
  // there's no cached session yet.
  const cached = React.useMemo(() => loadSession(), []);
  const [session, setSession] = React.useState<CachedSession | null>(cached);
  const [authError, setAuthError] = React.useState<string | undefined>();
  const [initializing, setInitializing] = React.useState<boolean>(naaCapable && !cached);

  // Dedup concurrent refresh calls — multiple API errors should not trigger
  // multiple sign-ins. The first 401 starts a refresh, subsequent ones await it.
  const refreshInFlight = React.useRef<Promise<string> | null>(null);

  // Cache the session so the next open renders instantly (both strategies).
  // For NAA this is only a render-time hint — MSAL stays the source of truth
  // for the token, and a stale cached token self-heals via the 401 refresh.
  const persist = React.useCallback((s: CachedSession | null) => {
    saveSession(s);
  }, []);

  // Acquire a session, surfacing UI (NAA popup / dialog) when `interactive`.
  // Tries NAA first; on a hard NAA failure (anything other than
  // interaction-required) it downgrades to the dialog flow and retries there.
  // `forceRefresh` bypasses MSAL's cached token (set on a 401 recovery).
  const acquireSession = React.useCallback(
    async (interactive: boolean, forceRefresh = false): Promise<CachedSession> => {
      if (strategyRef.current === "naa") {
        try {
          return await naaSignIn(interactive, forceRefresh);
        } catch (e) {
          if (isInteractionRequired(e)) throw e;
          console.warn("[auth] NAA unusable; falling back to Dialog auth:", e);
          strategyRef.current = "dialog";
        }
      }
      return runAuthDialog();
    },
    []
  );

  // NAA: silently acquire/refresh the token on mount. When we already seeded a
  // cached session this runs in the background (no "Signing in…" pane) and just
  // refreshes the token; on a cold first open it's what populates the session.
  // We call naaSignIn directly (not acquireSession) so a failure here never
  // auto-opens the dialog on load — that's reserved for the explicit Sign in
  // click. We do NOT downgrade the strategy here: a transient load-time blip
  // shouldn't abandon NAA for the whole session. The real downgrade happens in
  // acquireSession on the Sign in click / refresh if NAA is genuinely unusable.
  React.useEffect(() => {
    if (!naaCapable) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await naaSignIn(false);
        if (!cancelled) {
          setSession(s);
          saveSession(s);
        }
      } catch (e) {
        // Keep any cached session we rendered with; an API 401 will refresh it.
        console.warn("[auth] NAA silent sign-in failed:", e);
      } finally {
        if (!cancelled) setInitializing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [naaCapable]);

  const handleSignIn = async () => {
    setAuthError(undefined);
    try {
      const s = await acquireSession(true);
      persist(s);
      setSession(s);
    } catch (e: any) {
      setAuthError(e.message ?? String(e));
    }
  };

  const handleSignOut = () => {
    if (strategyRef.current === "naa") void naaSignOut();
    saveSession(null);
    setSession(null);
  };

  const refreshSession = React.useCallback(async (): Promise<string> => {
    if (refreshInFlight.current) return refreshInFlight.current;
    const p = (async () => {
      try {
        // Silent-first (naaSignIn tries silent before any popup); interaction
        // is allowed so a truly-expired MSAL session can recover without a hard
        // sign-out. forceRefresh bypasses the cached token that just 401'd.
        const s = await acquireSession(true, true);
        persist(s);
        setSession(s);
        return s.accessToken;
      } catch (e: any) {
        console.warn("[auth] refreshSession failed:", e?.message ?? e);
        // Clear the session only when re-auth is genuinely required, so a
        // transient failure (network blip) doesn't sign the user out
        // mid-session — the original random-sign-out complaint.
        if (isInteractionRequired(e) || strategyRef.current === "dialog") {
          persist(null);
          setSession(null);
        }
        throw e;
      } finally {
        refreshInFlight.current = null;
      }
    })();
    refreshInFlight.current = p;
    return p;
  }, [acquireSession, persist]);

  if (naaCapable && initializing) {
    return (
      <section className="auth-prompt">
        <h1>EPIC</h1>
        <p>Signing in…</p>
      </section>
    );
  }

  if (!session) {
    return <AuthPrompt onSignIn={handleSignIn} error={authError} />;
  }

  return (
    <SessionContext.Provider
      value={{
        username: session.username,
        accessToken: session.accessToken,
        graphAccessToken: session.graphAccessToken,
        refreshSession,
      }}
    >
      <App onSignOut={handleSignOut} />
    </SessionContext.Provider>
  );
}

Office.onReady((info) => {
  if (info.host !== Office.HostType.Outlook) return;
  const container = document.getElementById("root")!;
  const root = ReactDOM.createRoot(container);
  root.render(<Root />);
});
